// Mobile mega-menu submenu data (PRJT-119).
//
// The desktop mega panels (MegaServices/MegaTrichology/MegaSalons/MegaShop/
// MegaHelin) render rich, differently-shaped grids that don't fit a narrow
// mobile drawer. This module flattens each mega into a simple { to, label }
// link list so the mobile nav can show an accordion.
//
// SOURCE OF TRUTH:
//   - trichology + salons read the shared configs directly (no drift).
//   - services / shop / helin MIRROR the link arrays from their Mega* panels.
//     Keep these in sync with those components until the shared-source refactor
//     in PRJT-119 lands.

import { salons } from '../../config/salons'
import { trichologyDetails } from '../../config/trichologyDetails'
import { isFeatureEnabled, getDisabledBehavior } from '../../lib/featureFlags'
import type { FlagKey } from '../../config/flags'

export type MobileMegaKey = 'services' | 'trichology' | 'salons' | 'shop' | 'helin'

export interface MobileSubLink {
  to: string
  label: string
}

/** Minimal translator shape — just key → string. */
type Translate = (key: string) => string

// ── Mirrored arrays (keep in sync with the Mega* panels) ─────────────────────

const SERVICE_ITEMS: Array<{ slug: string; key: string }> = [
  { slug: 'womens-haircut', key: 'womensCut' },
  { slug: 'mens-haircut', key: 'mensCut' },
  { slug: 'colour-treatments', key: 'colour' },
  { slug: 'balayage-babylights', key: 'balayage' },
  { slug: 'blonde-specialist', key: 'blonde' },
  { slug: 'keratin-structure', key: 'keratin' },
  { slug: 'hair-care-repair', key: 'repair' },
  { slug: 'wash-blowdry', key: 'wash' },
  { slug: 'permanent-waves', key: 'permanent' },
  { slug: 'extensions', key: 'extensions' },
  { slug: 'hair-systems', key: 'systems' },
  { slug: 'brows-lashes', key: 'brows' },
  { slug: 'updos-makeup', key: 'updos' },
]

// Order mirrors MegaTrichology SLUGS.
const TRICHOLOGY_SLUGS = ['energy', 'purify', 'balancy', 'serenity']

// Mirrors MegaShop GROUPS (flattened). Duplicate keys across groups (styling,
// face) resolve to the same href and are de-duplicated below.
const SHOP_ITEMS: Array<{ key: string; q?: string }> = [
  { key: 'shampoo' },
  { key: 'conditioner' },
  { key: 'masksOils', q: 'mask' },
  { key: 'styling' },
  { key: 'naturalSkincare', q: 'skincare' },
  { key: 'face' },
  { key: 'body' },
  { key: 'hands', q: 'hand' },
  { key: 'foundation' },
  { key: 'cheeks', q: 'cheek' },
  { key: 'eyesBrows', q: 'brow' },
  { key: 'lips', q: 'lip' },
  { key: 'hairCare', q: 'men' },
  { key: 'menStyling', q: 'styling' },
  { key: 'menFace', q: 'face' },
  { key: 'beard' },
  { key: 'growth' },
  { key: 'pure' },
  { key: 'sweetSense', q: 'sweet' },
  { key: 'cactus' },
  { key: 'hair' },
  { key: 'skin' },
]

// Mirrors MegaHelin ABOUT + HELP + LEGAL (with the same flag visibility rule).
const HELIN_ITEMS: Array<{ path: string; labelKey: string; flag?: FlagKey }> = [
  { path: '/about', labelKey: 'megaHelin.items.about', flag: 'ABOUT' },
  { path: '/careers', labelKey: 'megaHelin.items.careers', flag: 'CAREERS' },
  { path: '/contact', labelKey: 'megaHelin.items.contact', flag: 'CONTACT' },
  { path: '/hair-length-guide', labelKey: 'megaHelin.items.hairGuide' },
  { path: '/gift-guide', labelKey: 'megaHelin.items.giftGuide', flag: 'GIFT_GUIDE' },
  { path: '/faq', labelKey: 'megaHelin.items.faq' },
  { path: '/cancellation-policy', labelKey: 'megaHelin.items.cancellation' },
  { path: '/privacy', labelKey: 'megaHelin.items.privacy' },
  { path: '/cookies', labelKey: 'megaHelin.items.cookies' },
]

function flagVisible(flag?: FlagKey): boolean {
  if (!flag) return true
  if (isFeatureEnabled(flag)) return true
  return getDisabledBehavior(flag) !== 'hide'
}

function dedupeByHref(links: MobileSubLink[]): MobileSubLink[] {
  const seen = new Set<string>()
  return links.filter((l) => (seen.has(l.to) ? false : (seen.add(l.to), true)))
}

/**
 * Flatten a mega key into the link list shown in the mobile nav accordion.
 * Returns [] for an unknown key. `lang` is the route-prefixed locale segment.
 */
export function getMobileSubmenu(mega: MobileMegaKey, lang: string, t: Translate): MobileSubLink[] {
  switch (mega) {
    case 'services':
      return SERVICE_ITEMS.map((it) => ({
        to: `/${lang}/services/${it.slug}`,
        label: t(`megaServices.items.${it.key}`),
      }))

    case 'trichology':
      return TRICHOLOGY_SLUGS.map((slug) => trichologyDetails.find((tx) => tx.slug === slug))
        .filter((tx): tx is NonNullable<typeof tx> => Boolean(tx))
        .map((tx) => ({ to: `/${lang}/trichology/${tx.slug}`, label: tx.name }))

    case 'salons':
      return salons.map((s) => ({ to: `/${lang}/salon/${s.id}`, label: s.name }))

    case 'shop':
      return dedupeByHref(
        SHOP_ITEMS.map((it) => ({
          to: `/${lang}/shop?q=${encodeURIComponent((it.q ?? it.key).toLowerCase())}`,
          label: t(`megaShop.items.${it.key}`),
        })),
      )

    case 'helin':
      return HELIN_ITEMS.filter((it) => flagVisible(it.flag)).map((it) => ({
        to: `/${lang}${it.path}`,
        label: t(it.labelKey),
      }))

    default:
      return []
  }
}
